// Calculation helpers for Sovereign Calculator
// Exports: DEFAULTS, futureValue, projectDust, dustToVacations, dustToLumina, computeScenarios

export const DEFAULTS = {
  periodsPerYear: 52,
  r_hold: 0.00, // 0% annual yield for holding
  m_trade: 1.3, // trading multiplier (30% better yield)
  // If Dust is earned per dollar contributed (mode: 'perDollar')
  dustPerDollar: 5, // Dust units earned per $ contributed (base = 5 dust)
  // If Dust is derived from a value (mode: 'yield'), use this conversion
  dustConversionFactor: 1, // multiply FV (dollar-equivalent) to get Dust units
  dustPerVacation: 1000, // Dust units needed for one vacation (RVP)
  luminaPerDustPerYear: 2, // LUMINA earned per Dust unit per year
  buyDustMultiplier: 3,
  sellDustMultiplier: 2,
};

/**
 * Future value of a series of recurring contributions
 * W = contribution per period (weekly if n=52)
 * r = annual nominal interest rate (decimal)
 * t = years
 * n = compounding periods per year
 */
export function futureValue(W, r, t, n = DEFAULTS.periodsPerYear) {
  const P = Number(W) || 0;
  const R = Number(r) || 0;
  const T = Number(t) || 0;
  const N = Number(n) || DEFAULTS.periodsPerYear;

  if (N <= 0 || T <= 0) return 0;

  if (R === 0) {
    // No interest: total contributions
    return P * N * T;
  }

  const ratePerPeriod = R / N;
  const periods = N * T;
  const factor = Math.pow(1 + ratePerPeriod, periods) - 1;
  return P * (factor / ratePerPeriod);
}

/**
 * Project Dust accumulation.
 * options:
 *  - mode: 'yield' | 'perDollar'
 *  - dustPerDollar: used when mode === 'perDollar'
 *  - dustConversionFactor: used when mode === 'yield' to convert FV($) to Dust
 */
export function projectDust({
  weeklyAmount = 0,
  annualRate = DEFAULTS.r_hold,
  years = 1,
  periodsPerYear = DEFAULTS.periodsPerYear,
  mode = "yield",
  dustPerDollar = DEFAULTS.dustPerDollar,
  dustConversionFactor = DEFAULTS.dustConversionFactor,
} = {}) {
  const W = Number(weeklyAmount) || 0;
  const r = Number(annualRate) || 0;
  const t = Number(years) || 0;
  const n = Number(periodsPerYear) || DEFAULTS.periodsPerYear;

  if (mode === "perDollar") {
    // Dust earned directly per dollar contributed (sum contributions * dustPerDollar)
    const totalContributed = W * n * t;
    return totalContributed * Number(dustPerDollar || 0);
  }

  // Default: Dust derived from the future value of contributions
  const fv = futureValue(W, r, t, n);
  return fv * Number(dustConversionFactor || 0);
}

export function dustToVacations(dustAmount, dustPerVacation = DEFAULTS.dustPerVacation) {
  const d = Number(dustAmount) || 0;
  const per = Number(dustPerVacation) || 1;
  return d / per;
}

export function dustToLumina(dustAmount, luminaPerDustPerYear = DEFAULTS.luminaPerDustPerYear) {
  const d = Number(dustAmount) || 0;
  const annual = d * Number(luminaPerDustPerYear || 0);
  return {
    annual,
    monthly: annual / 12,
  };
}

/**
 * Compute holding vs trading scenarios and helpers
 * Returns object with holdDust, tradeDust, diff, multiplier
 */
export function computeScenarios({
  weeklyAmount = 0,
  years = 1,
  periodsPerYear = DEFAULTS.periodsPerYear,
  r_hold = DEFAULTS.r_hold,
  m_trade = DEFAULTS.m_trade,
  mode = "yield",
  dustPerDollar = DEFAULTS.dustPerDollar,
  dustConversionFactor = DEFAULTS.dustConversionFactor,
  // additional trading assumptions
  fontanaPrice = 1.0,
  buysPerWeek = 1,
  sellsPerWeek = 0,
  sellGainPercent = 0.05,
  rvpPercent = 0.005,
  buyDustMultiplier = DEFAULTS.buyDustMultiplier,
  sellDustMultiplier = DEFAULTS.sellDustMultiplier,
} = {}) {
  let hold = projectDust({
    weeklyAmount,
    annualRate: r_hold,
    years,
    periodsPerYear,
    mode,
    dustPerDollar,
    dustConversionFactor,
  });

  const r_trade = r_hold * Number(m_trade || 1);
  let trade = projectDust({
    weeklyAmount,
    annualRate: r_trade,
    years,
    periodsPerYear,
    mode,
    dustPerDollar,
    dustConversionFactor,
  });

  const diff = trade - hold;
  const multiplier = hold === 0 ? (trade === 0 ? 1 : Infinity) : trade / hold;

  // Compute total Fontana bought over the period (dollars allocated to buys / buy price)
  const totalWeeks = periodsPerYear * years;
  const totalTradedDollars = weeklyAmount * totalWeeks;
  const buyFraction = (buysPerWeek + sellsPerWeek) > 0 ? (buysPerWeek / (buysPerWeek + sellsPerWeek)) : 1;
  const dollarsOnBuys = totalTradedDollars * buyFraction;
  const totalFontanaBought = fontanaPrice > 0 ? dollarsOnBuys / fontanaPrice : 0;

  // Compute total Fontana sold over the period (dollars allocated to sells / price)
  const sellFraction = (buysPerWeek + sellsPerWeek) > 0 ? (sellsPerWeek / (buysPerWeek + sellsPerWeek)) : 0;
  const dollarsOnSells = totalTradedDollars * sellFraction;
  const totalFontanaSold = fontanaPrice > 0 ? dollarsOnSells / fontanaPrice : 0;

  // RVP generated from trading dollars per new formula: USD amount * 10
  const rvpGenerated = totalTradedDollars * 10;

  // LUMINA generated: base applied to Fontana bought (totalFontanaBought * 5%)
  const luminaTotal = totalFontanaBought * 0.05;
  const luminaFromSells = {
    total: luminaTotal,
    annual: years > 0 ? luminaTotal / years : 0,
    monthly: years > 0 ? luminaTotal / (years * 12) : 0,
  };

  // If mode is perDollar, compute dust for hold and trade using buy/sell multipliers
  if (mode === 'perDollar') {
    const totalContributed = weeklyAmount * periodsPerYear * years;
    const holdPd = totalContributed * Number(dustPerDollar || 0);
    const dustFromBuys = dollarsOnBuys * Number(dustPerDollar || 0) * Number(buyDustMultiplier || 1);
    const dustFromSells = dollarsOnSells * Number(dustPerDollar || 0) * Number(sellDustMultiplier || 1);
    const tradePd = dustFromBuys + dustFromSells;
    hold = holdPd;
    trade = tradePd;
  }

  // Sells assumed to execute at sellGainPercent (not currently used in Dust calc)
  const estimatedSellProceeds = totalFontanaBought * fontanaPrice * (1 + Number(sellGainPercent || 0));

  return {
    hold,
    trade,
    diff,
    multiplier,
    totalFontanaBought,
    totalFontanaSold,
    rvpGenerated,
    luminaFromSells,
    estimatedSellProceeds,
  };
}

const sCalculatorHelpers = {
  DEFAULTS,
  futureValue,
  projectDust,
  dustToVacations,
  dustToLumina,
  computeScenarios,
};

export default sCalculatorHelpers;
